<template>

    <div v-cloak class="container-fluid">

        <div v-if="outage" id="body-container" class="card card-default portal-form-background my-2 p-2">
            <div class="row">
                <div class="col text-portal-secondary-red">
                    <h5>
                        We&apos;re making some updates to our system, and your prescription information is not available at this time. We apologize for the inconvenience. 
                    </h5>
                </div>
            </div>
        </div>

        <div v-else>

            <div class="row">
                <div class="col">
                    <h4 class="text-portal-soft-black pt-2">{{cmsContentBlocks['templateSelector.MainPage.Header']}}</h4>
                </div>
            </div>

            <div class="row">
                <div class="col">
                    <hr class="mt-0 mb-0 border-none taller-hr">
                </div>
            </div>

            <div class="row mt-3">
                <div class="col">
                    <h5 class="text-portal-soft-black" v-once>
                        {{cmsContentBlocks['preCertNameOfMedication.Header']}}
                    </h5>
                </div>
            </div>
            <div class="row">
                <div class="col">
                    <span class="text-portal-soft-red" v-once>{{cmsContentBlocks['allFieldsAreRequiredHeaderImportant']}}</span>
                    <span class="text-portal-soft-black" v-once>{{cmsContentBlocks['allFieldsAreRequiredHeader']}}</span>
                </div>
            </div>

            <div class="row mt-2 mt-lg-0">
                <div class="col">

                    <div class="row d-none d-lg-block">
                        <div class="col">
                            <hr>
                        </div>
                    </div>

                    <div v-if="sendToThisMailbox" class="row text-portal-soft-black mb-2">
                        <div class="col" v-once>
                            To: {{sendToThisMailbox}}
                        </div>
                    </div>

                    <div v-else>
                        <div class="row text-portal-soft-black mb-2">
                            <div class="col">
                                <label for="to-mailbox">{{cmsContentBlocks['preCertNameOfMedication.ToLabel']}}</label>
                            </div>
                        </div>

                        <div class="row mb-2">
                            <div class="col">
                                <select2-component key="drDropDown"
                                                   id="to-mailbox"
                                                   v-error-message="{ errorModel: 'errorModel.toMailboxId' }"
                                                   class="form-control" :allowclear="false" :addblankentry="false"
                                                   :options="physicianDropDown" v-model="messageModel.toMailboxId"
                                                   :placeholder="cmsContentBlocks['preCertNameOfMedication.ToDropDownPlaceHolder']">
                                </select2-component>
                                <span class="invalid-feedback is-invalid">{{ errorModel.toMailboxId | firstError }}</span>
                            </div>
                        </div>
                    </div>

                    <div v-if="showPhysicianDropDown">
                        <div class="row text-portal-soft-black mb-2">
                            <div class="col">
                                Physician
                            </div>
                        </div>

                        <div class="row mb-2">
                            <div class="col">
                                <select2-component key="physDropDown" title="Send message to this person" v-error-message="{ errorModel: 'errorModel.toPhysicianId' }" class="form-control" :allowclear="false" :addblankentry="false" :options="reassignPhysicianDropDown" v-model="messageModel.toPhysicianId" :placeholder="cmsContentBlocks['preCertNameOfMedication.ToDropDownPlaceHolder']">
                                </select2-component>
                            </div>
                        </div>
                    </div>


                    <div class="row text-portal-soft-black mb-2">
                        <div class="col" v-once>
                            <label for="name-of-medication">{{cmsContentBlocks['preCertNameOfMedication.NameOfMedicationLabel']}}</label>
                        </div>
                    </div>

                    <div class="row mb-2">
                        <div class="col">
                            <select2-component :allowclear="true"
                                               id="name-of-medication"
                                               v-error-message="{ errorModel: 'errorModel.drugId' }"
                                               url="/api/Medication/MedicationSearchDropDown"
                                               v-model="messageModel.drugId"
                                               :placeholder="cmsContentBlocks['preCertNameOfMedication.NameOfMedicationDropDownPlaceHolder']"
                                               class="form-control">
                            </select2-component>
                            <span class="invalid-feedback is-invalid">{{ errorModel.drugId | firstError }}</span>
                        </div>
                    </div>

                    <div v-if="messageModel.drugId">
                        <div class="row text-portal-soft-black mb-2">
                            <div class="col" v-once>
                                <label for="quantity-amount">{{cmsContentBlocks['preCertNameOfMedication.QuantityLabel']}}</label>
                            </div>
                        </div>

                        <div class="row text-portal-soft-black mb-2">
                            <div class="col">
                                <number-input-component mode="double"
                                                        id="quantity-amount"
                                                        v-model="messageModel.quantity"
                                                        :placeholder="cmsContentBlocks['preCertNameOfMedication.QuantityTextBoxPlaceHolder']"
                                                        v-error-message="{ errorModel: 'errorModel.quantity' }" class="form-control"
                                                        maxlength="10"></number-input-component>

                                <span class="invalid-feedback is-invalid">{{ errorModel.quantity | firstError }}</span>
                            </div>
                        </div>
                    </div>

                    <div class="row text-portal-soft-black mb-2">
                        <div class="col" v-once>
                            <label for="name-of-insurance-company">{{cmsContentBlocks['preCertNameOfMedication.NameOfInsuranceLabel']}}</label>
                        </div>
                    </div>

                    <div class="row text-portal-soft-black mb-2">
                        <div class="col">
                            <input id="name-of-insurance-company" type="text" :placeholder="cmsContentBlocks['preCertNameOfMedication.NameOfInsuranceTextBoxPlaceHolder']" v-model="messageModel.nameOfInsuranceCompany" v-error-message="{ errorModel: 'errorModel.nameOfInsuranceCompany' }" class="form-control" maxlength="150" />
                            <span class="invalid-feedback is-invalid">{{ errorModel.nameOfInsuranceCompany | firstError }}</span>
                        </div>
                    </div>

                    <div class="row text-portal-soft-black mb-2">
                        <div class="col" v-once>
                            <label for="policy-number">{{cmsContentBlocks['preCertNameOfMedication.PolicyNumberLabel']}}</label>
                        </div>
                    </div>

                    <div class="row text-portal-soft-black mb-2">
                        <div class="col">
                            <input id="policy-number"
                                   type="text" :placeholder="cmsContentBlocks['preCertNameOfMedication.PolicyNumberTextBoxPlaceHolder']"
                                   v-model="messageModel.policyNumber"
                                   v-error-message="{ errorModel: 'errorModel.policyNumber' }"
                                   class="form-control" maxlength="100" />
                            <span class="invalid-feedback is-invalid">{{ errorModel.policyNumber | firstError }}</span>
                        </div>
                    </div>

                    <div class="row text-portal-soft-black mb-2">
                        <div class="col" v-once>
                            <label for="phone-for-pre-cert">{{cmsContentBlocks['preCertNameOfMedication.PhoneNumberLabel']}}</label>
                        </div>
                    </div>

                    <div class="row text-portal-soft-black mb-2">
                        <div class="col">
                            <input type="text"
                                   id="phone-for-pre-cert"
                                   v-mask="usaPhoneNumberFormat"
                                   v-error-message="{ errorModel: 'errorModel.phoneNumber' }"
                                   class="form-control" maxlength="25"
                                   v-model="messageModel.phoneNumber"
                                   :placeholder="cmsContentBlocks['preCertNameOfMedication.PhoneNumberTextBoxPlaceHolder']" />
                            <span class="invalid-feedback is-invalid">{{ errorModel.phoneNumber | firstError }}</span>
                        </div>
                    </div>

                </div>

            </div>

        </div>
    </div>
</template>

<script>

    import Vue from 'vue';
    import formattingDirective from '@SharedVueComponents/formatting/formattingdirective';
    import errorMessageDirective from '@SharedVueComponents/Validation/ErrorMessageDirective';
    import select2Component from '@SharedVueComponents/select2/select2.vue';
    import { mask } from 'vue-the-mask'
    import { usaPhoneNumberFormat } from '@SharedServices/formatting.service';
    import numberInput from '@SharedVueComponents/NumberInputs/NumberInput.vue';
    import { UnityHttpSvc } from '@SharedHttpServices/unity.httpservice';

    export default {
        name: 'preCertPrescriptionMedication',
        props: {
            physicianDropDown: { type: Array },
            errorModel: { type: Object },
            cmsContentBlocks: { type: Object },
            //if set this is for ITC, DTC, etc. The "To Mailbox Id" will not be shown
            sendToThisMailbox: { type: String },
            //list of Reassign Physicians
            reassignPhysicianDropDown: { type: Array },
            //show or hide Reassign Physicians drop down
            showPhysicianDropDown: { type: Boolean }
        },
        components: {
            'select2-component': select2Component,
            'number-input-component': numberInput,
        },
        directives: {
            mask
        },
        data: function () {
            return {
                usaPhoneNumberFormat,

                messageModel: {
                    toMailboxId: null, //mailbox id
                    toPhysicianId: "", //physician id
                    drugId: null,
                    quantity: null,
                    phoneNumber: null,
                    nameOfInsuranceCompany: null,
                    policyNumber: null
                },

                outage: false

            }
        },
        created: function () {
            UnityHttpSvc.IsAvailable()
                .done(response => {
                    this.outage = response && response.outage && response.outage === true;
                    if (this.outage === true) {
                        this.$emit('hideSend');
                    }
                })
        },
        mounted: function () {
            this.$emit('setMessageObject', this.messageModel);
        },
        watch: {

            'messageModel.drugId': function (newValue, oldValue) {
                Vue.delete(this.errorModel, 'quantity');
                this.messageModel.quantity = null;
            },

            messageModel: {
                handler: function (newValue) {
                    this.$emit('setMessageObject', newValue);
                },
                deep: true
            },
        }
    };
</script>

